import React from 'react';
import { gql } from '@apollo/client';
import {
  Datagrid,
  BooleanInput,
  List,
  TextField,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  FunctionField,
  useListContext,
} from 'react-admin';
import { choicesFromEnum, choicesForProKinds } from 'utils/helpers';
import { ProfessionalQualificationStatus, ProfessionalPoolStatus } from 'typings/enums';
import { ENUMS } from 'components/Enum/enums';
import LinkField from 'components/LinkField';
import { IdFilter } from 'components/Filter';

const POOL_STATUS_LABEL_BY_VALUE = Object.fromEntries(
  Object.entries(ProfessionalPoolStatus).map(([label, value]) => [value, label]),
) as Record<string, string>;

const poolStatusLabel = (status?: string) =>
  status ? POOL_STATUS_LABEL_BY_VALUE[status] ?? status : '';

const CredentialStatusFilter = (_) => {
  const { filterValues } = useListContext();

  if (!filterValues.search?.credential_types?.length) {
    return null;
  }

  return (
    <SelectInput
      alwaysOn
      label="Credential Status"
      source="search.qualification_status"
      choices={choicesFromEnum(ProfessionalQualificationStatus)}
    />
  );
};

const WageLogsListFilters = [
  <IdFilter alwaysOn label="Professional ID" source="search.professional_id" />,
  <IdFilter alwaysOn label="Location ID" source="search.location_id" />,
  <IdFilter alwaysOn label="Company ID" source="search.company_id" />,
  <SelectInput
    alwaysOn
    label="Pool Status"
    source="search.pool_status"
    choices={choicesFromEnum(ProfessionalPoolStatus)}
  />,
  <SelectInput
    alwaysOn
    label="Kind"
    source="search.kinds"
    emptyText="All"
    parse={(kinds) => (kinds ? [kinds] : undefined)}
    choices={choicesForProKinds(ENUMS.Professional.kind)}
  />,
  <ReferenceInput reference="Qualification" source="search.credential_types" alwaysOn>
    <AutocompleteInput label="Credential Types" multiple optionText="long_name" />
  </ReferenceInput>,
  <CredentialStatusFilter alwaysOn />,
  <ReferenceInput label="Position" source="search.position_id" reference="Position" alwaysOn>
    <AutocompleteInput optionText="display_name" />
  </ReferenceInput>,
  <SelectInput
    alwaysOn
    choices={[
      { id: true, name: 'W-2' },
      { id: false, name: '1099' },
    ]}
    source="search.is_w2"
    label="Tax Type"
  />,
  <IdFilter alwaysOn label="Pro Pool ID" source="search.pro_pool_id" />,
  <BooleanInput
    alwaysOn
    source="search.all_positions_satisfied"
    label="All Conditions Satisfied"
  />,
  <BooleanInput alwaysOn source="search.show_rejected" label="Show rejected" />,
];

const ProPoolProfessionalsList = () => (
  <List filters={WageLogsListFilters} exporter={false}>
    <Datagrid bulkActionButtons={false}>
      <FunctionField
        label="Pro ID"
        render={(r) => (
          <LinkField to={`/Professional/${r?.professional_id}/show`}>
            {r?.professional_id}
          </LinkField>
        )}
      />
      <TextField label="Name" source="name" sortable={false} />
      <FunctionField
        label="Location ID"
        render={(r) => (
          <LinkField to={`/Location/${r?.location_id}/show`}>{r?.location_id}</LinkField>
        )}
      />
      <FunctionField
        label="Pro Pool ID"
        render={(r) => (
          <LinkField to={`/Pro_pool/${r?.pool_id}/show`}>{r?.pool_id}</LinkField>
        )}
      />
      <FunctionField
        label="Position(s)"
        sortBy="position"
        render={(record) => record?.positions?.map((item) => item.name).join(', ')}
      />
      <TextField label="Kind" source="kind" sortable={false} />
      <FunctionField
        label="Pool Status"
        sortable={false}
        render={(record) => poolStatusLabel(record?.status)}
      />
      <FunctionField
        label="Missing Conditions"
        sortBy="unsatisfied_conditions"
        render={(record) => record?.unsatisfied_conditions?.map((item) => item.name).join(', ')}
      />
      <TextField label="Tax Type" source="billing_type" sortable={false} />
    </Datagrid>
  </List>
);

ProPoolProfessionalsList.query = gql`
  query GET_LIST($input: ProPoolDashboardInput) {
    proPools: getProPoolDashboard(input: $input) {
      id
      professional_id
      name
      kind
      billing_type
      status
      pool_id
      location_id
      positions {
        id
        name
      }
      unsatisfied_conditions {
        id
        name
      }
    }
    _proPoolsMeta: _getProPoolDashboardMeta(input: $input) {
      count
    }
  }
`;

export default ProPoolProfessionalsList;
